import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/BudgetPlanner/helpers'
import TableOptions from '@/models/TableOptions'
import { getSortedItems, omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import { generatePassword } from '@/helpers/functions'
import budgetPlanRepository from '@/repository/generatedRepository/budgetPlanRepository'
import { attachParents } from '@/helpers/reports'
import { recalcAfterFilter } from '@/components/budget-planner/budget-planner'
import pick from 'lodash/pick'
import moment from 'moment'
const { saveAs } = require('file-saver')

const STATUS = {
  APPROVED: 'approved',
  NOT_APPROVED: 'not_approved',
  NEED_APPROVAL: 'need_approval',
  NO_REQUEST: 'no_request',
}

const VIEW_OPTION = {
  HAS_SPEND: 'hasSpend',
  HAS_FTD: 'hasFtd',
  HAS_REQUEST: 'hasRequest',
  ALL: 'all',
}

// Предикат видимости листа (fg без детей или ffg) для каждого Table view.
// request_budget может прийти строкой — приводим к числу (как в defineStatus/recalc)
const VIEW_OPTION_LEAF_FILTER = {
  [VIEW_OPTION.HAS_SPEND]: row => !!row.has_spend,
  [VIEW_OPTION.HAS_FTD]: row => !!row.has_ftd,
  [VIEW_OPTION.HAS_REQUEST]: row => +row.request_budget > 0,
}

const GROUPS_ORDER = {
  advertiser: ['advertiser', 'country', 'partner', 'affiliateId', 'trafficSource'],
  partner: ['partner', 'advertiser', 'country', 'trafficSource'],
}

const TOTAL_KEYS = [
  'spend_1',
  'spend_2',
  'spend_3',
  'request_budget',
  'approve_budget',
  'current_fact',
  'balance',
]

function defineStatus({ request_budget, approve_budget }) {
  if (!(request_budget > 0)) return STATUS.NO_REQUEST
  if (approve_budget > 0) return STATUS.APPROVED
  if (approve_budget === 0) return STATUS.NOT_APPROVED
  return STATUS.NEED_APPROVAL
}

// Бэкенд отдаёт spend_approve через PHP number_format — строкой ("46.74", "1,234.56").
// Без приведения к числу ListBudgetPlanner не форматирует значение (нет % и округления),
// а сортировка/экспорт работают со строкой
function toMetricNumber(value) {
  if (typeof value !== 'string') return value
  const number = Number(value.replace(/,/g, ''))
  return value.trim() !== '' && !isNaN(number) ? number : value
}

function prepareRowDates(row, currentMonth) {
  row.balance = (row.approve_budget ?? 0) - (row.current_fact ?? 0)
  const months = Array.from({ length: 3 }, (_, i) => moment(currentMonth).add(i, 'months').format('YYYY-MM'))
  months.forEach(month => {
    if (!row.dates[month]) {
      row.dates[month] = { spend: 0, spend_approve: 0, cfd: 0, roi: 0 }
    }
  })
  months.forEach((month, i) => {
    Object.keys(row.dates[month]).forEach(subKey => {
      row[`${subKey}_${i + 1}`] = toMetricNumber(row.dates[month][subKey])
    })
  })
}

function processRow(row, currentGroup, levelIndex, currentMonth) {
  row.server_id = row.id
  row.id = generatePassword(8)
  currentGroup.forEach((key, i) => {
    row[key] = i === levelIndex ? row.title : undefined
  })
  prepareRowDates(row, currentMonth)
}

function processTreeData(data, currentGroup, currentMonth) {
  data.forEach(row => {
    processRow(row, currentGroup, 0, currentMonth)
    row.sg.forEach(sg => {
      processRow(sg, currentGroup, 1, currentMonth)
      sg.tg.forEach(tg => {
        processRow(tg, currentGroup, 2, currentMonth)
        tg.fg.forEach(fg => {
          processRow(fg, currentGroup, 3, currentMonth)
          if (fg.ffg?.length) {
            fg.ffg.forEach(ffg => {
              processRow(ffg, currentGroup, 4, currentMonth)
              ffg.status = defineStatus(ffg)
            })
            fg._allFfg = [...fg.ffg]
          } else {
            fg.status = defineStatus(fg)
          }
        })
        tg._allFg = [...tg.fg]
      })
      sg._allTg = [...sg.tg]
    })
    row._allSg = [...row.sg]
  })
}

function calcTotal(data) {
  const seed = Object.fromEntries(TOTAL_KEYS.map(k => [k, 0]))
  return data.reduce((acc, item) => {
    TOTAL_KEYS.forEach(key => {
      acc[key] += item[key] || 0
    })
    return acc
  }, seed)
}

function applyViewOptionFilter(tg, viewOption) {
  const leafFilter = VIEW_OPTION_LEAF_FILTER[viewOption]
  tg.fg.length = 0
  tg._allFg.forEach(fg => {
    // fg.ffg на этот момент ещё хранит результат предыдущего прохода фильтрации
    // (перестраивается ниже), поэтому детей нужно проверять
    // по полному fg._allFfg, а не по уже урезанному fg.ffg
    const visible = !leafFilter || leafFilter(fg) || !!fg._allFfg?.some(leafFilter)
    if (!visible) return
    tg.fg.push(fg)
    if (fg._allFfg) {
      fg.ffg = leafFilter ? fg._allFfg.filter(leafFilter) : [...fg._allFfg]
    }
  })
}

function fgPassesFilters(fg, { approveFrom, approveTo, balanceFrom, balanceTo, factFrom, factTo, status }) {
  return (
    (approveFrom === null || fg.approve_budget >= approveFrom) &&
    (approveTo === null || fg.approve_budget <= approveTo) &&
    (balanceFrom === null || fg.balance >= balanceFrom) &&
    (balanceTo === null || fg.balance <= balanceTo) &&
    (factFrom === null || fg.current_fact >= factFrom) &&
    (factTo === null || fg.current_fact <= factTo) &&
    (status.length === 0 || status.includes(fg.status))
  )
}

const state = {
  list: [],
  listFiltered: [],
  total: {},
  filters: StorageService.get(storageKeys.BUDGET_PLANNER_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  total: state => state.total,
  list: state => state.list,
  listFiltered: state => state.listFiltered,
  listSorted: (state, getters) =>
    getSortedItems(getters.listFiltered, {
      sortBy: state.filters.sortBy1,
      sortDesc: state.filters.sortDesc1,
    }),
  isOpenFilterPanel: state => state.filters.clientOptions?.filterPanelExpanded,
  isDataLoaded: state => state.list.length > 0,
}

const actions = {
  async loadData(context, filtersData) {
    const { signal, ...filters } = filtersData
    const syncedFilters = {
      ...filters,
      clientOptions: { ...filters.clientOptions, viewOption: filters.filter.viewOption },
    }
    context.commit('setFilters', syncedFilters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, syncedFilters)

    const appliedFilters = omitEmptyDeepFilters(
      pick(filters.filter, [
        'advertisers',
        'managers',
        'partners',
        'countries',
        'paymentModels',
        'offerCategories',
        'date',
        'group',
        'groups',
        'multigeoBreakdown',
      ]),
    )
    const preparedFilters = prepareFilters({ ...filters, filter: appliedFilters })
    const data = (
      await budgetPlanRepository.budgetPlanner({
        ...preparedFilters.filter,
        signal,
      })
    ).data
    attachParents(data)
    context.commit('setList', { data, filters: syncedFilters })
    context.commit('applyFilters')
    return { products: data }
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.BUDGET_PLANNER_FILTERS)
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = { ...getters.filters, clientOptions }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, updatedFilters)
  },
  updatePaginationGeneralReport(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, filters)
    context.commit('setPagination', filters.pagination)
  },
  updateBudget(context, { id, ...data }) {
    return budgetPlanRepository.updateBudget(id, data)
  },
  createBudget(context, params) {
    return budgetPlanRepository.createBudget(params)
  },
  blockRequest(context, groups) {
    return budgetPlanRepository.blockRequest(groups)
  },

  importFile(context, { formData, date, config }) {
    return budgetPlanRepository.import(formData, date, config)
  },
  async downloadImportIssuesFile(context, id) {
    const response = await budgetPlanRepository.downloadImportIssuesFile(id)
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    saveAs(blob, `import_issues_${id}.xlsx`)
    return response
  },
  updateSortGeneralReport(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, filters)
    context.commit('setSort', {
      sortBy: filters.sortBy,
      sortDesc: filters.sortDesc,
    })
  },
  updateViewOption(context, viewOption) {
    const updatedFilters = {
      ...context.getters.filters,
      filter: { ...context.getters.filters.filter, viewOption },
      clientOptions: { ...context.getters.filters.clientOptions, viewOption },
    }
    context.commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, updatedFilters)
    context.commit('applyFilters')
  },
  updateClientFilters(context, filters) {
    const updatedFilters = {
      ...context.getters.filters,
      filter: { ...context.getters.filters.filter, ...filters },
      clientOptions: { ...context.getters.filters.clientOptions, viewOption: filters.viewOption },
    }
    context.commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.BUDGET_PLANNER_FILTERS, updatedFilters)
    context.commit('applyFilters')
  },
}

const mutations = {
  setList(state, { data, filters }) {
    if (data === null) {
      state.list = []
      return
    }
    const currentMonth = moment(filters.filter.date).startOf('month').subtract(3, 'months')
    const currentGroup = GROUPS_ORDER[filters.filter.group]
    processTreeData(data, currentGroup, currentMonth)
    state.list = [...data]
    state.total = calcTotal(data)
    state.filters.clientOptions = {
      ...state.filters.clientOptions,
      viewOption: filters.filter.viewOption,
    }
  },

  applyFilters(state) {
    const f = state.filters.filter
    const viewOption = state.filters.clientOptions.viewOption
    const rangeFilters = {
      approveFrom: f.approveFrom !== '' ? f.approveFrom : null,
      approveTo: f.approveTo !== '' ? f.approveTo : null,
      balanceFrom: f.balanceFrom !== '' ? f.balanceFrom : null,
      balanceTo: f.balanceTo !== '' ? f.balanceTo : null,
      factFrom: f.factFrom !== '' ? f.factFrom : null,
      factTo: f.factTo !== '' ? f.factTo : null,
      status: Array.isArray(f.status) ? f.status : [],
    }

    const roots = [...state.list]

    for (const root of roots) {
      root.sg.length = 0
      root._allSg.forEach(sg => {
        root.sg.push(sg)
        sg.tg.length = 0
        sg._allTg.forEach(tg => {
          sg.tg.push(tg)
          applyViewOptionFilter(tg, viewOption)
        })
      })
    }
    for (let r = roots.length - 1; r >= 0; r--) {
      const root = roots[r]
      for (let s = root.sg.length - 1; s >= 0; s--) {
        const sg = root.sg[s]
        for (let t = sg.tg.length - 1; t >= 0; t--) {
          const tg = sg.tg[t]
          for (let fgi = tg.fg.length - 1; fgi >= 0; fgi--) {
            const fg = tg.fg[fgi]
            if (fg._allFfg) {
              for (let ffgi = fg.ffg.length - 1; ffgi >= 0; ffgi--) {
                if (!fgPassesFilters(fg.ffg[ffgi], rangeFilters)) {
                  fg.ffg.splice(ffgi, 1)
                }
              }
              if (fg.ffg.length === 0) tg.fg.splice(fgi, 1)
            } else {
              if (!fgPassesFilters(fg, rangeFilters)) tg.fg.splice(fgi, 1)
            }
          }
          if (tg.fg.length === 0) sg.tg.splice(t, 1)
        }
        if (sg.tg.length === 0) root.sg.splice(s, 1)
      }
      if (root.sg.length === 0) roots.splice(r, 1)
    }
    recalcAfterFilter(roots, state.total)
    state.listFiltered = roots
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  setPagination(state, pagination) {
    state.tableOptions = {
      ...state.tableOptions,
      page: pagination.page,
      pagination,
      totalItems: Math.ceil(state.tableOptions.total / pagination.limit),
    }
  },
  setSort(state, { sortBy, sortDesc }) {
    state.tableOptions = {
      ...state.tableOptions,
      sortBy,
      sortDesc,
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
